import React, { useEffect, useState } from "react";

import { getPlanningDeadlines } from "../../api";
import { MONTH_LABEL } from "./cellFormat";

// What is actually coming up. The capacity grid says a cell holds four jobs;
// it cannot say which four, and that is the question a manager asks next.
//
// Rows arrive already scoped and already sorted soonest-first by the same
// helpers the grid reads through. Nothing here re-filters or re-sorts them:
// that is precisely how this list would start disagreeing with the grid
// beside it, and a list that disagrees with the cell above it is worse than
// no list at all.

const CATEGORY_LABEL = {
  personal: "Personal",
  corporate: "Corporate",
  tslips: "T-Slips",
  other: "Other",
};

const groupByMonth = (rows) => {
  const groups = [];
  rows.forEach((row) => {
    const last = groups[groups.length - 1];
    if (last && last.month === row.month) last.rows.push(row);
    else groups.push({ month: row.month, rows: [row] });
  });
  return groups;
};

const CELL = {
  padding: "8px 10px",
  borderBottom: "1px solid #F3F4F6",
  whiteSpace: "nowrap",
};

const MUTED = { color: "#6B7280", fontSize: 13 };

export default function DeadlinesView({ months, categories, employee }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  // `categories` is a new array on every render of the parent, so the effect
  // depends on its joined string rather than the array itself -- otherwise
  // this refetches on every keystroke elsewhere on the page.
  const categoryParam = (categories || []).join(",");

  useEffect(() => {
    let cancelled = false;
    const params = { months };
    if (categoryParam) params.categories = categoryParam;
    if (employee != null) params.employee = employee;

    getPlanningDeadlines(params)
      .then((data) => {
        if (cancelled) return;
        setRows(data);
        setError("");
      })
      .catch(() => {
        if (cancelled) return;
        setRows([]);
        setError("Could not load the deadlines list.");
      });

    return () => {
      cancelled = true;
    };
  }, [months, categoryParam, employee]);

  if (error) return <p style={{ color: "#B91C1C" }}>{error}</p>;
  if (rows === null) return <p style={MUTED}>Loading…</p>;
  if (!rows.length) {
    return (
      <p style={MUTED}>
        Nothing due in this window. Widen the months, or clear a filter.
      </p>
    );
  }

  return (
    <div>
      {groupByMonth(rows).map((group) => (
        <section key={group.month} style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, margin: "0 0 6px" }}>
            {MONTH_LABEL(group.month)}
            <span style={{ ...MUTED, fontWeight: 400, marginLeft: 8 }}>
              {group.rows.length} due
            </span>
          </h3>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{ borderCollapse: "collapse", width: "100%", minWidth: 640 }}
            >
              <thead>
                <tr>
                  {["Due", "Task", "Client", "Category", "Status", "Assigned to"]
                    .map((h) => (
                      <th
                        key={h}
                        style={{ ...CELL, textAlign: "left", color: "#6B7280" }}
                      >
                        {h}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row) => (
                  <tr key={`${row.kind}-${row.id}`}
                      data-testid={`deadline-row-${row.kind}-${row.id}`}>
                    <td style={CELL}>{row.due_date || "—"}</td>
                    <td style={{ ...CELL, whiteSpace: "normal" }}>{row.name}</td>
                    <td style={{ ...CELL, whiteSpace: "normal" }}>
                      {row.client || ""}
                    </td>
                    <td style={CELL}>
                      {CATEGORY_LABEL[row.category] || row.category}
                    </td>
                    <td style={CELL}>{row.status || "—"}</td>
                    {/* An empty assignee list is work waiting to be given to
                        someone, which is worth saying out loud rather than
                        leaving as a blank cell that reads like missing data. */}
                    <td style={CELL}>
                      {row.assignees.length ? (
                        row.assignees.join(", ")
                      ) : (
                        <span style={{ color: "#B45309" }}>Unassigned</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </div>
  );
}
